import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./SizeGuide.css";
import { sizes } from "../data/sizes";

export default function SizeGuide() {
  const [tab, setTab] = useState("fan");
  const [altura, setAltura] = useState("");
  const [peso, setPeso] = useState("");
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const current = sizes[tab];

  useEffect(() => {
    if (rowRef.current) {
      rowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [resultado]);

  useEffect(() => {
    setResultado(null);
    setAltura("");
    setPeso("");
  }, [tab]);

  const rowRef = useRef(null);
  const parseAltura = (valor) => {
    if (!valor) return { value: 0, error: "Informe a altura" };

    let v = valor.toString().replace(",", ".");
    const num = Number(v);

    if (!num) return { value: 0, error: "Altura inválida" };

    // metros → cm
    const alturaCm = num < 3 ? num * 100 : num;

    if (alturaCm < 120 || alturaCm > 220) {
      return { value: 0, error: "Altura fora do padrão (120cm - 220cm)" };
    }

    return { value: alturaCm, error: null };
  };

  const parsePeso = (valor) => {
    if (!valor) return { value: 0, error: "Informe o peso" };

    let v = valor.toString().replace(",", ".");
    const num = Number(v);

    if (!num) return { value: 0, error: "Peso inválido" };

    if (num < 30 || num > 200) {
      return { value: 0, error: "Peso fora do padrão (30kg - 200kg)" };
    }

    return { value: num, error: null };
  };

  const calcularTamanho = () => {
    setErro(null);

    const altParsed = parseAltura(altura);
    const pesoParsed = parsePeso(peso);

    if (altParsed.error) {
      setErro(altParsed.error);
      setResultado(null);
      return;
    }

    if (pesoParsed.error && current.data[0].peso) {
      setErro(pesoParsed.error);
      setResultado(null);
      return;
    }

    const alt = altParsed.value;
    const pes = pesoParsed.value;

    let melhor = null;
    let menorDistancia = Infinity;

    current.data.forEach((item) => {
      const altMedia = (item.altura[0] + item.altura[1]) / 2;

      const pesoMedia = item.peso ? (item.peso[0] + item.peso[1]) / 2 : 0;

      const distAlt = Math.abs(alt - altMedia);
      const distPeso = item.peso ? Math.abs(pes - pesoMedia) : 0;

      const distanciaTotal = distAlt + distPeso;

      if (distanciaTotal < menorDistancia) {
        menorDistancia = distanciaTotal;
        melhor = item.size;
      }
    });

    setResultado(melhor || "Não encontrado");
  };

  const handleAlturaChange = (e) => {
    let value = e.target.value;

    const numeric = Number(value.replace(",", "."));

    if (numeric > 250) return;

    value = value.replace(/[^0-9.,]/g, "");

    if (value.length > 6) return;

    setAltura(value);
  };

  const handlePesoChange = (e) => {
    let value = e.target.value;
    const numeric = Number(value.replace(",", "."));

    if (numeric > 250) return;

    value = value.replace(/[^0-9.,]/g, "");

    if (value.length > 5) return;

    setPeso(value);
  };

  const order = [
    "size",
    "idade",
    "altura",
    "comprimento",
    "largura",
    "cintura",
    "peso",
    "manga",
  ];

  const sizeMap = {
    S: "P",
    M: "M",
    L: "G",
    XL: "GG",
    XXL: "2XL",
    "2XL": "2XL",
    "3XL": "3XL",
  };
  return (
    <div className="container">
      <h1>Guia de Tamanhos</h1>

      {/* Tabs */}
      <div className="tabs">
        {Object.keys(sizes).map((key) => (
          <button
            key={key}
            className={tab === key ? "active" : ""}
            onClick={() => setTab(key)}
          >
            {sizes[key].icon} {sizes[key].label}
          </button>
        ))}
      </div>

      {/* Recomendação */}
      <div className="card">
        <h2>Descubra seu tamanho</h2>

        <div className="inputs">
          <input
            type="number"
            inputMode="decimal"
            placeholder="Ex: 179 ou 1,79"
            value={altura}
            onChange={handleAlturaChange}
          />

          <input
            type="text"
            inputMode="decimal"
            placeholder="Peso (ex: 80 ou 80,5)"
            value={peso}
            onChange={handlePesoChange}
          />
        </div>

        <button className="btn-calc" onClick={calcularTamanho}>
          Calcular
        </button>

        {resultado && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="result"
          >
            Tamanho recomendado: {sizeMap[resultado] || resultado}
          </motion.div>
        )}
        {erro && <div className="error">{erro}</div>}
      </div>

      {/* Tabela */}
      <div className="table-container">
        <AnimatePresence mode="wait">
          <motion.table
            key={tab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {(() => {
              const columns = order.filter(
                (col) => current.data[0][col] !== undefined
              );

              return (
                <>
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th key={col}>
                          {{
                            size: "Tamanho",
                            comprimento: "Comprimento",
                            largura: "Largura",
                            manga: "Manga",
                            altura: "Altura",
                            peso: "Peso",
                            idade: "Idade",
                          }[col] || col}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {current.data.map((item, index) => (
                      <tr
                        key={item.size}
                        ref={resultado === item.size ? rowRef : null}
                        className={
                          resultado === item.size ? "highlight-row" : ""
                        }
                      >
                        {columns.map((col) => (
                          <td key={col}>
                            {col === "size" ? (
                              <>
                                {sizeMap[item[col]] || item[col]}
                                {resultado === item.size && " 🔥"}
                              </>
                            ) : Array.isArray(item[col]) ? (
                              item[col][0] === item[col][1] ? (
                                item[col][0]
                              ) : (
                                `${item[col][0]} - ${item[col][1]}`
                              )
                            ) : (
                              item[col]
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </>
              );
            })()}
          </motion.table>
        </AnimatePresence>
      </div>
    </div>
  );
}
